// Deterministic randomness. The same seed always builds the same polder,
// which means "it looked better yesterday" is a bug, not a mood.

export function mulberry32(seed) {
    let a = seed >>> 0;
    return function () {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Cheap 3D integer hash in [0,1). Used for per-voxel colour jitter. */
export function hash3(x, y, z) {
    let h = Math.imul(x | 0, 0x8da6b343) ^ Math.imul(y | 0, 0xd8163841) ^ Math.imul(z | 0, 0xcb1ab31f);
    h = Math.imul(h ^ (h >>> 13), 0x85ebca6b);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
}

const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Seeded 2D value noise. Not the fastest, but we generate the world once.
 */
export function makeNoise2D(seed) {
    const rand = mulberry32(seed);
    const perm = new Uint8Array(512);
    const grad = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
        perm[i] = i;
        grad[i] = rand() * 2 - 1;
    }
    for (let i = 255; i > 0; i--) {
        const j = (rand() * (i + 1)) | 0;
        const t = perm[i];
        perm[i] = perm[j];
        perm[j] = t;
    }
    for (let i = 0; i < 256; i++) perm[i + 256] = perm[i];

    const at = (xi, yi) => grad[perm[(perm[xi & 255] + (yi & 255)) & 255]];

    return function noise2D(x, y) {
        const xi = Math.floor(x);
        const yi = Math.floor(y);
        const xf = fade(x - xi);
        const yf = fade(y - yi);
        const a = lerp(at(xi, yi), at(xi + 1, yi), xf);
        const b = lerp(at(xi, yi + 1), at(xi + 1, yi + 1), xf);
        return lerp(a, b, yf);
    };
}

/** Fractal Brownian motion over a noise2D function. */
export function makeFbm(noise, { octaves = 4, lacunarity = 2.0, gain = 0.5 } = {}) {
    return function fbm(x, y) {
        let amp = 1;
        let freq = 1;
        let sum = 0;
        let norm = 0;
        for (let o = 0; o < octaves; o++) {
            sum += noise(x * freq, y * freq) * amp;
            norm += amp;
            amp *= gain;
            freq *= lacunarity;
        }
        return sum / norm;
    };
}

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
export const smoothstep = (e0, e1, x) => {
    const t = clamp((x - e0) / (e1 - e0), 0, 1);
    return t * t * (3 - 2 * t);
};
export const mix = lerp;
